import React, { useState, useEffect, useMemo } from 'react';
import { FaUnlink } from 'react-icons/fa';
import { Col, Row } from 'react-bootstrap';
import api from 'services/api';
import Card from 'v3/components/Card';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import Fallback from 'v3/components/Fallback';
import DataTable from 'v3/components/DataTable';
import theme from 'v3/theme';
import AddEstablishment from './Modals/AddEstablishment';
import { useUser } from './context';

const userDetailsTableStyles = {
  borderCollapse: 'collapse',
  '> thead > tr > th': {
    borderBottom: '1px solid #E5E7EB',
  },
  '> tbody > tr': {
    borderBottom: '1px solid #E5E7EB',
  },
  '> tbody > tr:hover': {
    background: '#F5F6F8',
  },
  '> tbody > tr:hover > td': {
    background: '#F5F6F8',
  },
};

export default function UserEstablishments() {
  const user = useUser();

  const [show, setShow] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user.data?.id) user.setData({ establishments: [] });
  }, []);

  const isEditing = useMemo(() => {
    return !!user.data?.id;
  }, [user.data?.id]);

  function handleDeleteEstablishment(establishmentId) {
    user.setData({
      establishments: user.data?.establishments?.filter(
        (establishment) => establishment.id !== establishmentId,
      ),
    });
  }

  async function handleDetachEstablishment(establimentId) {
    try {
      setLoading(true);
      await api.delete(`v3/user/${user.data.id}/establishment/${establimentId}`);

      user.setData({
        establishments: user.data?.establishments.filter(
          (establishment) => establishment.id !== establimentId,
        ),
      });
    } catch (error) {
      user.showSnackbar('Erro ao excluir estabelecimento', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function handleRemoveEstablishment(establishmentId) {
    if (isEditing) handleDetachEstablishment(establishmentId);
    else handleDeleteEstablishment(establishmentId);
  }

  const establishmentsDatatable = useMemo(() => {
    const offset = (page - 1) * perPage;
    const establishment = user.data?.establishments
      ?.filter(
        (item) =>
          item.social_name.toLowerCase().includes(search.toLowerCase()) ||
          item.cgccpf.includes(search),
      )
      .slice(offset, perPage + offset);
    if (establishment?.length === 0 && page > 1) setPage(page - 1);
    return establishment;
  }, [user.data?.establishments, page, perPage, search]);

  return (
    <>
      <AddEstablishment isOpen={show} onClose={() => setShow(false)} />
      <Card
        loading={user.isFetching}
        header={
          <Text type="header" color="dark">
            Estabelecimentos
          </Text>
        }
        HeaderRightComponent={
          <>
            {!!user.data?.establishments?.length && (
              <Button
                onClick={() => {
                  user.updatePrevUser();
                  setShow(true);
                }}
                disabled={!user.hasPermissionToEdit}
              >
                <Text type="regular" weight="500" color="white">
                  Adicionar
                </Text>
              </Button>
            )}
          </>
        }
      >
        <Row>
          <Fallback
            fall={!user.data?.establishments?.length}
            component={
              <Col xs={12} className="align-items-center d-flex flex-column my-2">
                <Text color={theme.colors.dark}>
                  Não há estabelecimentos cadastrados nesse usuário.
                </Text>
                <Button
                  className="mt-4"
                  onClick={() => {
                    user.updatePrevUser();
                    setShow(true);
                  }}
                  disabled={!user.hasPermissionToEdit}
                >
                  <Text type="regular" weight="500" color="white">
                    Adicionar
                  </Text>
                </Button>
              </Col>
            }
          >
            <Col>
              <DataTable
                data={establishmentsDatatable}
                total={user.data?.establishments?.length}
                pages={Math.ceil(user.data?.establishments?.length / perPage)}
                currentPage={page}
                onPageChanged={setPage}
                onSearchChanged={setSearch}
                onPerPageChanged={(v) => setPerPage(+v)}
                loading={loading}
                searchPlaceholder="CNPJ ou Razão social"
                tableStyles={userDetailsTableStyles}
                columns={[
                  {
                    name: 'Estabelecimentos',
                    selector: (row) => row?.social_name,
                  },
                  { name: 'CPF/CNPJ', selector: (row) => row?.cgccpf },
                  {
                    name: 'Ação',
                    render: (row) => (
                      <Button
                        variant="error"
                        size="sm"
                        className="mx-1"
                        onClick={() => handleRemoveEstablishment(row.id)}
                        disabled={!user.hasPermissionToEdit}
                      >
                        <FaUnlink />
                      </Button>
                    ),
                  },
                ]}
              />
            </Col>
          </Fallback>
        </Row>
      </Card>
    </>
  );
}
